/**
 *
 * ResourcesList
 *
 */

import { Component } from 'react';
import PropTypes from 'prop-types';
import Text, { TEXT_SIZE, TEXT_COLOR, TEXT_WEIGHT } from '@components/Text';
import {
  TableHeader,
  StyledArrowUpIcon,
  StyledArrowDownIcon,
  Cell,
} from './styles';
import { ORDERS } from '@shared/constants';
import {connect} from "react-redux";
import {changeFilterType, clearResourcesData, getResources} from "@containers/Admin/shared/ResourcesWrapper/actions";
import propTypes from "prop-types";
import {compose} from "redux";
import Proptypes from "prop-types";
import {withRouter} from "react-router-dom";
import VirtualizedResourcesList from './VirtualizedResourcesList'

class ResourcesList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      order: null,
      sortColumnIndex: null,
    };
  }

  componentWillUnmount() {
    this.props.dispatchClearResourcesData()
  }

  isSortableColumn = (col) => {
    if (col && col.sortable) {
      return true;
    }
    // Backward compatibility: "Name" column is sortable by default
    return (col?.name || '').toLowerCase() === 'name';
  };

  getSortValueFn = (col) => {
    if (typeof col?.sortValue === 'function') {
      return col.sortValue;
    }
    // Backward compatibility: "Name" column sorts by item.name
    if ((col?.name || '').toLowerCase() === 'name') {
      return (item) => item?.name;
    }
    return null;
  };

  setSortColumn = (columnIndex, tableConf) => {
    const col = tableConf?.[columnIndex];
    if (!this.isSortableColumn(col)) {
      return;
    }
    this.setState(({ sortColumnIndex, order }) => {
      if (sortColumnIndex !== columnIndex) {
        return { sortColumnIndex: columnIndex, order: ORDERS.ASC };
      }
      return { sortColumnIndex: columnIndex, order: order === ORDERS.ASC ? ORDERS.DESC : ORDERS.ASC };
    });
  };

  sortItems = (items, tableConf) => {
    const { order, sortColumnIndex } = this.state;
    if (sortColumnIndex === null || !order) {
      return items;
    }
    const col = tableConf?.[sortColumnIndex];
    const sortValueFn = this.getSortValueFn(col);
    if (!sortValueFn) {
      return items;
    }

    const normalize = (value) => {
      if (value === null || value === undefined) return null;
      if (value instanceof Date) return value.getTime();
      if (typeof value === 'string') return value.toLowerCase();
      if (typeof value === 'boolean') return value ? 1 : 0;
      return value;
    };

    const compare = (a, b) => {
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      if (typeof a === 'number' && typeof b === 'number') return a - b;
      return String(a).localeCompare(String(b));
    };

    const decorated = items.map((item, index) => {
      let value = null;
      try {
        value = sortValueFn(item);
      } catch (e) {
        value = null;
      }
      return { item, index, value: normalize(value) };
    });

    decorated.sort((x, y) => {
      const diff = compare(x.value, y.value);
      if (diff !== 0) {
        return order === ORDERS.ASC ? diff : -diff;
      }
      // stable sort
      return x.index - y.index;
    });

    return decorated.map(({ item }) => item);
  };

  renderHeader = (spread, tableConf) => {
    const { order, sortColumnIndex } = this.state;
    const Arrow = order === ORDERS.ASC ? StyledArrowDownIcon : StyledArrowUpIcon;
    const InactiveArrow = StyledArrowDownIcon;
    return (
      <TableHeader spread={spread}>
        {tableConf.map((col, index) => {
          const colName = col.name;
          const isSortable = this.isSortableColumn(col);
          const isActive = sortColumnIndex === index;
          const align = col?.textOptions?.align || col?.align;
          const alignStyle = (() => {
            switch (align) {
              case 'center':
                return { justifyContent: 'center', textAlign: 'center' };
              case 'right':
                return { justifyContent: 'flex-end', textAlign: 'right' };
              case 'left':
              default:
                return { justifyContent: 'flex-start', textAlign: 'left' };
            }
          })();
          return (
          <Cell
            key={`${index}${colName}`}
            onClick={isSortable ? () => this.setSortColumn(index, tableConf) : undefined}
            style={{ ...(isSortable ? { cursor: 'pointer' } : {}), ...alignStyle }}
          >
            <Text
              size={TEXT_SIZE.T4}
              textColor={isActive ? TEXT_COLOR.ACTIVE : TEXT_COLOR.DISABLED}
              weight={TEXT_WEIGHT.BOLD}
            >
              {colName}
            </Text>
            {isSortable && (
              isActive
                ? <Arrow style={{ opacity: 1 }} />
                : <InactiveArrow style={{ opacity: 0.25 }} />
            )}
          </Cell>
        );
        })}
      </TableHeader>
    );
  };

  render() {
    const { items, tableConf, selectedItem, showSelectionArrow } = this.props;
    const spread = tableConf
      .map((col) => {
        if (col.width) {
          return typeof col.width === 'number' ? `${col.width}px` : col.width;
        }
        return 'auto';
      })
      .join(' ')
      .concat(showSelectionArrow ? ' 30px' : '');
    const displayData = tableConf.map(({ getDisplay, textOptions = {} }) => ({
      getDisplay,
      textOptions,
    }));
    const sortedItems = this.sortItems([...(items || [])], tableConf);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, minWidth: 0 }}>
        <div style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'hidden' }}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, width: 'max-content', minWidth: '100%' }}>
            {this.renderHeader(spread, tableConf)}
            <div style={{ flex: 1, minHeight: 0 }}>
              <VirtualizedResourcesList
                  items={sortedItems}
                  spread={spread}
                  displayData={displayData}
                  selectedItem={selectedItem}
                  showSelectionArrow={showSelectionArrow}
                  resource={this.props.resource}
                  fetchMore={this.props.dispatchLoadFilteredResources}
                  onSelect={this.props.onSelect}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ResourcesList.defaultProps = {
  onSelect: () => {},
  showSelectionArrow: true,
};

ResourcesList.propTypes = {
  history: Proptypes.object,
  items: PropTypes.array.isRequired,
  tableConf: PropTypes.array.isRequired,
  onSelect: PropTypes.func,
  selectedItem: PropTypes.object,
  showSelectionArrow: PropTypes.bool,
  dispatchLoadFilteredResources: propTypes.func,
  dispatchChangeFilterType: propTypes.func,
  dispatchClearResourcesData: propTypes.func,
};

const mapDispatchToProps = (dispatch) => {
  return {
    dispatchLoadFilteredResources: (type, search, loadType) => dispatch(getResources(type, search, loadType)),
    dispatchChangeFilterType: (isTemplate) => dispatch(changeFilterType(isTemplate)),
    dispatchClearResourcesData: () => dispatch(clearResourcesData())
  }
}

const mapStateToProps = state => {
  return {
    resource: state.toJS()?.resourceManager
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)

export default compose(withConnect, withRouter)(ResourcesList);
